import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <header className="privacy-header">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Effective Date: February 23, 2026</p>
        </header>

        <div className="privacy-content">
          <p>Cadence IO LLC, doing business as Cadence IO ("Cadence IO," "we," "our," or "us"), respects your privacy and is committed to protecting personal information processed through our website, platform, and related services.</p>

          <p>This Privacy Policy explains how we collect, use, disclose, and protect information when you access:</p>
          <ul>
            <li>cadenceio.com and related subdomains</li>
            <li>Our hosted SaaS platform</li>
            <li>Associated integrations, APIs, documentation, and tools</li>
            <li>Any beta features or pre-release services</li>
          </ul>
          <p>(collectively, the "Services").</p>
          <p>By accessing or using the Services, you acknowledge this Privacy Policy.</p>

          {/* Section 1 */}
          <h2>1. Important Notice Regarding Beta Services</h2>
          <div className="note-box">
            <p>The Services are currently offered in beta phase. Features may change, be modified, limited, or discontinued at any time. Beta Services are provided "as is" and may contain errors, interruptions, or performance limitations. Data handling practices may evolve as the Services develop.</p>
          </div>

          <div className="section-divider"></div>

          {/* Section 2 */}
          <h2>2. Role of Cadence IO (B2B Services)</h2>
          <p>Cadence IO provides a B2B SaaS platform used by record labels, artist managers, distributors, and related businesses to manage and transmit marketing and pitch materials to Digital Service Providers ("DSPs").</p>
          <p>When we process personal information on behalf of a business customer:</p>
          <ul>
            <li>The customer is the "controller" or "business" determining the purposes and means of processing.</li>
            <li>Cadence IO acts as a "service provider" or "processor" under applicable law.</li>
            <li>We process such data solely in accordance with our agreements and this Privacy Policy.</li>
          </ul>

          <div className="section-divider"></div>

          {/* Section 3 */}
          <h2>3. Information We Collect</h2>
          <p>We collect information in the following categories:</p>

          <h3>A. Content Submitted by Customers ("Content")</h3>
          <p>"Content" includes marketing materials, pitch data, assets, artist information, release details, metadata, analytics data, and other materials submitted through the Services.</p>
          <p><strong>You retain ownership of your Content.</strong></p>
          <p><strong>We do not claim ownership of your Content.</strong></p>

          <h3>User Responsibility for Content</h3>
          <p>You represent and warrant that:</p>
          <ul>
            <li>You own or have all necessary rights, licenses, permissions, and authority to submit Content.</li>
            <li>Submission of Content does not violate any intellectual property, publicity, privacy, or contractual rights.</li>
            <li>You are authorized to transmit Content to designated DSPs.</li>
          </ul>
          <p>Cadence IO does not monitor, verify, or validate Content and assumes no responsibility for its legality, accuracy, or rights clearance.</p>

          <h3>B. Account & Customer Information</h3>
          <p>We collect information when you:</p>
          <ul>
            <li>Create or administer an account</li>
            <li>Use the Services</li>
            <li>Contact support</li>
            <li>Participate in research or marketing events</li>
            <li>Integrate third-party services</li>
          </ul>
          <p>This may include:</p>

          <p><strong>Identifiers</strong></p>
          <ul>
            <li>Name</li>
            <li>Business email</li>
            <li>Username</li>
            <li>Company name</li>
            <li>Job title</li>
          </ul>

          <p><strong>Account Data</strong></p>
          <ul>
            <li>Login credentials</li>
            <li>OAuth tokens</li>
            <li>Linked third-party accounts</li>
            <li>Payment details (processed via third-party processors)</li>
          </ul>

          <p><strong>Usage Information</strong></p>
          <ul>
            <li>Device information</li>
            <li>IP address</li>
            <li>Log data</li>
            <li>Interaction data</li>
            <li>Analytics</li>
          </ul>

          <p><strong>Communications</strong></p>
          <ul>
            <li>Support inquiries</li>
            <li>Recorded calls (where permitted by law)</li>
          </ul>

          <p><strong>Professional Information</strong></p>
          <ul>
            <li>Employer</li>
            <li>Industry affiliation</li>
          </ul>

          <h3>C. Third-Party Integrations & OAuth</h3>
          <p>The Services allow integrations with third-party providers, including Digital Service Providers and email providers.</p>
          <p>If you choose to connect third-party services:</p>
          <ul>
            <li>You authorize us to access information necessary to enable the integration.</li>
            <li>You remain subject to the third party's privacy policies and terms.</li>
            <li>We are not responsible for third-party platform practices or availability.</li>
          </ul>

          <h3>Google API & Gmail Integration</h3>
          <p>Our use of information received from Google APIs complies with the Google API Services User Data Policy, including Limited Use requirements.</p>
          <p>If you connect Gmail via OAuth:</p>
          <ul>
            <li>We request access solely to send email messages initiated by you.</li>
            <li>We do not read, monitor, or store Gmail inbox contents except as necessary to transmit outgoing emails.</li>
            <li>We do not use Google user data for advertising.</li>
            <li>We do not sell or transfer Google user data.</li>
            <li>OAuth tokens are stored using industry-standard encryption and access controls.</li>
            <li>Access may be revoked at any time through your Google Account settings.</li>
          </ul>
          <p>We are not responsible for Google's platform availability, modifications, or restrictions.</p>

          <div className="section-divider"></div>

          {/* Section 4 */}
          <h2>4. How We Use Information</h2>
          <p>We use information to:</p>

          <h3>A. Provide the Services</h3>
          <ul>
            <li>Operate and maintain the platform</li>
            <li>Transmit Content to DSPs as directed</li>
            <li>Provide customer support</li>
            <li>Manage accounts</li>
            <li>Process transactions</li>
          </ul>

          <h3>B. Improve and Secure the Services</h3>
          <ul>
            <li>Analyze performance and usage trends</li>
            <li>Develop product enhancements</li>
            <li>Detect and prevent fraud or misuse</li>
            <li>Protect system integrity</li>
          </ul>

          <h3>C. Legal & Compliance Purposes</h3>
          <ul>
            <li>Comply with applicable law</li>
            <li>Respond to lawful requests</li>
            <li>Enforce agreements</li>
            <li>Protect rights and property</li>
          </ul>

          <h3>D. Marketing Communications</h3>
          <p>We may send product updates or marketing communications. You may opt out at any time.</p>
          <p><strong>We do not sell personal information.</strong></p>

          <div className="section-divider"></div>

          {/* Section 5 */}
          <h2>5. How We Disclose Information</h2>
          <p>We may disclose identifiers and professional information to:</p>
          <ul>
            <li>Vendors providing hosting, analytics, payment processing, or customer support</li>
            <li>DSPs and recipients you designate</li>
            <li>Affiliates for operational purposes</li>
            <li>As required by law or legal process</li>
            <li>In connection with a merger, acquisition, restructuring, or asset sale</li>
            <li>With your consent</li>
          </ul>
          <p>We require service providers to process data consistent with contractual obligations and applicable law.</p>

          <div className="section-divider"></div>

          {/* Section 6 */}
          <h2>6. Data Retention</h2>
          <p>We retain personal information only as long as necessary to:</p>
          <ul>
            <li>Provide the Services</li>
            <li>Comply with legal obligations</li>
            <li>Resolve disputes</li>
            <li>Enforce agreements</li>
            <li>Protect legal rights</li>
          </ul>
          <p>Upon account termination:</p>
          <ul>
            <li>Login credentials and active access tokens are deleted within thirty (30) days.</li>
            <li>Certain transactional or business records may be retained as required by law.</li>
            <li>Aggregated or anonymized data may be retained indefinitely for analytics and product improvement.</li>
          </ul>
          <p>Backup copies may persist temporarily pursuant to standard archival practices.</p>

          <div className="section-divider"></div>

          {/* Section 7 */}
          <h2>7. Cookies & Tracking Technologies</h2>
          <p>Cadence IO uses cookies and similar technologies to operate, secure, and improve the Services. This section explains what these technologies are, how we use them, and your choices regarding their use.</p>

          <h3>A. What Are Cookies and Similar Technologies?</h3>
          <p>Cookies are small text files stored on your device when you visit a website. We may also use:</p>
          <ul>
            <li>Web beacons (pixel tags)</li>
            <li>Local storage technologies</li>
            <li>Log files</li>
            <li>Device identifiers</li>
            <li>Analytics SDKs (where applicable)</li>
          </ul>
          <p>These technologies help us recognize users, understand usage patterns, and maintain system functionality.</p>

          <h3>B. Categories of Cookies We Use</h3>
          <p>Because Cadence IO operates as a B2B SaaS platform, our use of cookies is limited to business-related purposes. We may use the following categories:</p>

          <p><strong>i. Strictly Necessary Cookies</strong></p>
          <p>These cookies are essential for:</p>
          <ul>
            <li>Platform authentication</li>
            <li>Account login sessions</li>
            <li>Security and fraud prevention</li>
            <li>Load balancing and infrastructure management</li>
          </ul>
          <p>These cookies cannot be disabled because the Services cannot function properly without them.</p>

          <p><strong>ii. Performance & Analytics Cookies</strong></p>
          <p>We use analytics tools, including Google Analytics (GA4), to understand how users interact with our website and platform.</p>
          <p>Analytics tools may collect:</p>
          <ul>
            <li>IP address (which may be truncated or pseudonymized where supported)</li>
            <li>Device and browser information</li>
            <li>Pages visited</li>
            <li>Features used</li>
            <li>Time spent within the platform</li>
            <li>Referring URLs</li>
            <li>Aggregated usage metrics</li>
          </ul>
          <p>We use this information to:</p>
          <ul>
            <li>Analyze performance and usage trends</li>
            <li>Improve platform functionality</li>
            <li>Diagnose technical issues</li>
            <li>Maintain security and reliability</li>
          </ul>
          <p>We do not use analytics data to build advertising profiles or for cross-context behavioral advertising.</p>
          <p>Additional information about how Google processes data is available at: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></p>
          <p>Users may opt out of Google Analytics by using the Google Analytics Opt-Out Browser Add-On made available by Google.</p>

          <p><strong>iii. Functional Cookies</strong></p>
          <p>These cookies allow the Services to remember preferences and settings, such as:</p>
          <ul>
            <li>User interface selections</li>
            <li>Session continuity</li>
            <li>Platform configuration settings</li>
          </ul>

          <h3>C. No Targeted Advertising or Sale of Personal Data</h3>
          <p>Cadence IO does not:</p>
          <ul>
            <li>Sell personal data</li>
            <li>Share personal data for cross-context behavioral advertising</li>
            <li>Use cookies for targeted advertising as defined under applicable privacy laws</li>
            <li>Engage in profiling in furtherance of decisions that produce legal or similarly significant effects</li>
          </ul>
          <p>If our data practices change in the future, we will update this Privacy Policy and implement required opt-out mechanisms where applicable.</p>

          <h3>D. Data Minimization and Proportionality</h3>
          <p>We use cookies and similar technologies only to the extent reasonably necessary and proportionate to:</p>
          <ul>
            <li>Provide the Services</li>
            <li>Secure and maintain system integrity</li>
            <li>Analyze and improve product performance</li>
            <li>Support legitimate business operations</li>
          </ul>
          <p>We do not collect information through cookies for purposes materially unrelated to those described in this Policy.</p>

          <h3>E. Your Choices</h3>
          <p>You may control cookies through:</p>
          <ul>
            <li>Browser settings that allow you to block or delete cookies</li>
            <li>Device-level controls</li>
            <li>Third-party opt-out tools (such as Google's opt-out browser add-on)</li>
          </ul>
          <p>Please note that disabling certain cookies may affect platform functionality, authentication, or performance.</p>
          <p>Where required by applicable law (including GDPR where applicable), we will obtain consent before deploying non-essential cookies.</p>

          <h3>F. Do Not Track & Universal Opt-Out Signals</h3>
          <p>Some browsers transmit "Do Not Track" signals. Because Cadence IO does not engage in the sale of personal data or targeted advertising, we do not currently respond to such signals.</p>
          <p>If we engage in activities that require recognition of Universal Opt-Out Mechanisms under applicable law, we will update this section accordingly.</p>

          <div className="section-divider"></div>

          {/* Section 8 */}
          <h2>8. Security</h2>
          <p>We implement commercially reasonable administrative, technical, and organizational safeguards designed to protect information.</p>
          <p>However, no system is completely secure. We do not guarantee absolute security of transmitted or stored data.</p>
          <p>In the event of a confirmed security incident involving personal information, we will provide notice as required by applicable law.</p>

          <div className="section-divider"></div>

          {/* Section 9 */}
          <h2>9. International Transfers</h2>
          <p>Information may be processed in jurisdictions outside your location.</p>
          <p>Where required, we rely on appropriate legal transfer mechanisms such as:</p>
          <ul>
            <li>Standard contractual clauses</li>
            <li>Adequacy decisions</li>
            <li>Contractual safeguards</li>
          </ul>
          <p>You may request additional information regarding transfer mechanisms.</p>

          <div className="section-divider"></div>

          {/* Section 10 */}
          <h2>10. Your Rights</h2>
          <p>Depending on your jurisdiction, you may have rights to:</p>
          <ul>
            <li>Access personal information</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion</li>
            <li>Restrict processing</li>
            <li>Object to certain uses</li>
            <li>Receive data portability</li>
          </ul>
          <p>Requests may be submitted to: <a href="mailto:admin@cadenceio.com">admin@cadenceio.com</a></p>
          <p>We may require verification before responding.</p>

          <div className="section-divider"></div>

          {/* Section 11 */}
          <h2>11. Children</h2>
          <p>The Services are intended for business use only and are not directed to individuals under 18. We do not knowingly collect personal information from children.</p>

          <div className="section-divider"></div>

          {/* Section 12 */}
          <h2>12. Third-Party Services Disclaimer</h2>
          <p>The Services depend on third-party platforms, including DSPs and OAuth providers. We are not responsible for:</p>
          <ul>
            <li>Third-party service outages</li>
            <li>API restrictions or changes</li>
            <li>Data handling by third parties</li>
            <li>Loss of functionality due to external platform changes</li>
          </ul>

          <div className="section-divider"></div>

          {/* Section 13 */}
          <h2>13. No Monitoring Obligation</h2>
          <p>Cadence IO has no obligation to monitor, review, verify, or validate Content submitted through the Services. Users are solely responsible for compliance with applicable laws and third-party requirements.</p>

          <div className="section-divider"></div>

          {/* Section 14 */}
          <h2>14. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. Material changes will be communicated via email or platform notice. Continued use of the Services after updates constitutes acceptance.</p>

          <div className="section-divider"></div>

          {/* Section 15 - Contact */}
          <div className="contact-info">
            <h3>15. Contact Information</h3>
            <p>Cadence IO LLC</p>
            <p>4485 Songbird Lane, Evergreen, CO 80439</p>
            <p><strong>Email:</strong> <a href="mailto:admin@cadenceio.com">admin@cadenceio.com</a></p>
          </div>

          <div className="section-divider"></div>

          {/* CCPA / CPRA Section */}
          <h2>California Privacy Rights (CCPA / CPRA)</h2>
          <p>This section applies to California residents and supplements the Privacy Policy.</p>

          <h3>1. Categories of Personal Information Collected</h3>
          <p>In the preceding 12 months, we may have collected the following categories (as defined by the California Consumer Privacy Act, as amended by the California Privacy Rights Act):</p>
          <ul>
            <li><strong>Identifiers</strong> (name, email, IP address, account identifiers)</li>
            <li><strong>Customer records information</strong> (business contact details, account credentials)</li>
            <li><strong>Commercial information</strong> (service subscriptions and transactions)</li>
            <li><strong>Internet or network activity</strong> (usage logs, analytics data)</li>
            <li><strong>Professional or employment information</strong></li>
            <li><strong>Audio/visual information</strong> (if voluntarily submitted or recorded where permitted)</li>
            <li><strong>Inferences</strong> drawn from usage data</li>
          </ul>
          <p>We do not collect sensitive personal information for purposes requiring a right to limit use under CPRA.</p>

          <h3>2. Sources of Personal Information</h3>
          <p>We collect personal information from:</p>
          <ul>
            <li>You directly</li>
            <li>Your employer or organization</li>
            <li>Your use of the Services</li>
            <li>Integrated third-party platforms</li>
            <li>Service providers and analytics tools</li>
          </ul>

          <h3>3. Business or Commercial Purposes</h3>
          <p>We collect and use personal information to:</p>
          <ul>
            <li>Provide and operate the Services</li>
            <li>Secure and improve the platform</li>
            <li>Communicate with customers</li>
            <li>Comply with legal obligations</li>
            <li>Prevent fraud and misuse</li>
          </ul>

          <h3>4. Sale or Sharing of Personal Information</h3>
          <p><strong>Cadence IO does not sell personal information.</strong></p>
          <p><strong>Cadence IO does not share personal information for cross-context behavioral advertising.</strong></p>
          <p>We do not knowingly sell or share personal information of individuals under 16.</p>

          <h3>5. Disclosure to Service Providers</h3>
          <p>We may disclose personal information to service providers and contractors for business purposes, including hosting, payment processing, analytics, and support. Such disclosures are governed by written contracts restricting use to specified business purposes consistent with California law.</p>

          <h3>6. California Privacy Rights</h3>
          <p>California residents may have the right to:</p>
          <ul>
            <li>Know what personal information we collect and disclose</li>
            <li>Access specific pieces of personal information</li>
            <li>Request deletion of personal information</li>
            <li>Correct inaccurate personal information</li>
            <li>Opt out of sale or sharing (not applicable because we do not sell or share)</li>
            <li>Limit use of sensitive personal information (not applicable as we do not use it for additional purposes)</li>
            <li>Not be discriminated against for exercising privacy rights</li>
          </ul>
          <p>To submit a request, email: <a href="mailto:admin@cadenceio.com">admin@cadenceio.com</a></p>
          <p>We may verify your identity before fulfilling requests. We will respond within the time required by law.</p>

          <div className="section-divider"></div>

          {/* EU / UK / EEA Section */}
          <h2>European Union / UK / EEA Privacy Notice</h2>
          <p><em>(GDPR Article 13 & 14 Compliant Disclosure)</em></p>
          <p>This section applies where the General Data Protection Regulation ("GDPR") or UK GDPR applies.</p>

          <h3>1. Data Controller and Processor Roles</h3>
          <p>When Cadence IO provides Services to business customers:</p>
          <ul>
            <li>The business customer is the Data Controller.</li>
            <li>Cadence IO acts as a Data Processor.</li>
          </ul>
          <p>For personal data relating to our own marketing, website analytics, and account administration, Cadence IO acts as Data Controller.</p>
          <p>Contact: <a href="mailto:admin@cadenceio.com">admin@cadenceio.com</a></p>

          <h3>2. Legal Bases for Processing</h3>
          <p>Where Cadence IO acts as Controller, we rely on:</p>
          <ul>
            <li><strong>Contractual necessity (Article 6(1)(b))</strong> — to provide Services.</li>
            <li><strong>Legitimate interests (Article 6(1)(f))</strong> — to improve, secure, and administer the Services.</li>
            <li><strong>Legal obligation (Article 6(1)(c))</strong> — compliance requirements.</li>
            <li><strong>Consent (Article 6(1)(a))</strong> — where required (e.g., marketing emails in certain jurisdictions).</li>
          </ul>
          <p>Where we act as Processor, we process personal data on documented instructions from the Controller.</p>

          <h3>3. Categories of Data</h3>
          <ul>
            <li>Identification and contact data</li>
            <li>Professional data</li>
            <li>Usage and technical data</li>
            <li>Account credentials</li>
            <li>Content submitted by customers</li>
          </ul>

          <h3>4. Recipients of Personal Data</h3>
          <p>Personal data may be disclosed to:</p>
          <ul>
            <li>Hosting providers</li>
            <li>Analytics providers</li>
            <li>Payment processors</li>
            <li>Customer-designated DSPs</li>
            <li>Legal authorities where required</li>
          </ul>

          <h3>5. International Transfers</h3>
          <p>Data may be transferred outside the EEA or UK. Where required, we rely on:</p>
          <ul>
            <li>European Commission Standard Contractual Clauses</li>
            <li>UK International Data Transfer Addendum</li>
            <li>Adequacy decisions</li>
            <li>Contractual safeguards</li>
          </ul>

          <h3>6. Retention</h3>
          <p>We retain personal data only as long as necessary for:</p>
          <ul>
            <li>Service provision</li>
            <li>Legal compliance</li>
            <li>Dispute resolution</li>
            <li>Enforcement of agreements</li>
          </ul>
          <p>Aggregated or anonymized data may be retained indefinitely.</p>

          <h3>7. Data Subject Rights</h3>
          <p>Subject to applicable law, individuals may have the right to:</p>
          <ul>
            <li>Access personal data</li>
            <li>Rectify inaccurate data</li>
            <li>Erase data</li>
            <li>Restrict processing</li>
            <li>Object to processing</li>
            <li>Data portability</li>
            <li>Withdraw consent</li>
            <li>Lodge a complaint with a supervisory authority</li>
          </ul>
          <p>Requests may be submitted to <a href="mailto:admin@cadenceio.com">admin@cadenceio.com</a>.</p>

          <div className="section-divider"></div>

          {/* DPA Section */}
          <h2>Data Processing Addendum (DPA)</h2>
          <p>This Data Processing Addendum ("DPA") forms part of the SaaS Agreement between the customer ("Controller") and Cadence IO LLC ("Processor").</p>

          <h3>1. Scope</h3>
          <p>This DPA applies where Processor processes Personal Data on behalf of Controller in connection with the Services.</p>

          <h3>2. Nature and Purpose of Processing</h3>
          <p>Processor processes Personal Data solely to:</p>
          <ul>
            <li>Provide the Services</li>
            <li>Transmit Content to DSPs as directed</li>
            <li>Provide customer support</li>
            <li>Secure and maintain the platform</li>
          </ul>

          <h3>3. Duration</h3>
          <p>Processing continues for the duration of the Agreement and for limited post-termination retention periods consistent with the Privacy Policy.</p>

          <h3>4. Categories of Data Subjects</h3>
          <ul>
            <li>Customer employees</li>
            <li>Artists and representatives</li>
            <li>Marketing personnel</li>
            <li>Business contacts</li>
          </ul>

          <h3>5. Types of Personal Data</h3>
          <ul>
            <li>Names</li>
            <li>Email addresses</li>
            <li>Professional titles</li>
            <li>Account identifiers</li>
            <li>Usage data</li>
            <li>Content metadata</li>
          </ul>

          <h3>6. Processor Obligations</h3>
          <p>Processor shall:</p>
          <ul>
            <li>Process data only on documented instructions</li>
            <li>Ensure confidentiality obligations bind personnel</li>
            <li>Implement appropriate technical and organizational safeguards</li>
            <li>Assist Controller with data subject requests where required</li>
            <li>Notify Controller of confirmed personal data breaches without undue delay</li>
            <li>Delete or return personal data upon termination (subject to legal retention requirements)</li>
          </ul>

          <h3>7. Subprocessors</h3>
          <p>Processor may engage subprocessors for hosting, analytics, and support.</p>
          <p>Processor shall:</p>
          <ul>
            <li>Maintain written agreements with subprocessors</li>
            <li>Impose data protection obligations consistent with this DPA</li>
            <li>Remain responsible for subprocessor compliance</li>
          </ul>
          <p>A current list of subprocessors will be provided upon request.</p>

          <h3>8. International Transfers</h3>
          <p>Where Personal Data is transferred outside the EEA/UK:</p>
          <ul>
            <li>Standard Contractual Clauses shall apply.</li>
            <li>The UK Addendum shall apply where required.</li>
          </ul>

          <h3>9. Security Measures</h3>
          <p>Processor implements commercially reasonable safeguards including:</p>
          <ul>
            <li>Encryption in transit and at rest where appropriate</li>
            <li>Access controls</li>
            <li>Authentication protections</li>
            <li>Logging and monitoring practices</li>
          </ul>
          <p>No system is guaranteed to be completely secure.</p>

          <h3>10. Liability</h3>
          <p>Liability under this DPA is subject to the limitations of liability set forth in the SaaS Agreement.</p>

        </div>
      </div>

      <a href="#top" className="back-to-top" aria-label="Back to top">↑</a>
    </div>
  );
};

export default PrivacyPolicy;
